import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { CONTENT } from "@/content";

const spanFor = (i) => {
  // bento pattern
  const map = ["md:col-span-2 md:row-span-2", "", "", "md:row-span-2", "", "md:col-span-2"];
  return map[i % map.length];
};

export default function Gallery() {
  const [active, setActive] = useState(null);
  const { heading, note, images } = CONTENT.gallery;

  return (
    <section className="px-6 md:px-16 py-28 md:py-40" data-testid="gallery-section">
      <div className="mb-16 max-w-2xl">
        <span className="font-body text-xs uppercase tracking-[0.4em]" style={{ color: "var(--gold)" }}>
          gallery
        </span>
        <h2 className="font-serif-d tracking-tightest text-5xl md:text-7xl font-light mt-4">
          {heading}
        </h2>
        <p className="font-body mt-4 text-sm" style={{ color: "var(--muted-rose)" }}>
          {note}
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[180px] md:auto-rows-[220px] gap-3 md:gap-5">
        {images.map((img, i) => (
          <motion.button
            key={i}
            className={`group relative overflow-hidden rounded-2xl ${spanFor(i)}`}
            style={{ border: "1px solid rgba(232,180,184,0.12)" }}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: (i % 3) * 0.1 }}
            onClick={() => setActive(img)}
            data-testid={`gallery-item-${i}`}
          >
            <img
              src={img.url}
              alt={img.caption}
              loading="lazy"
              style={{ objectPosition: img.pos || "center top" }}
              className="w-full h-full object-cover grayscale-[0.4] sepia-[0.15] transition-[filter,transform] duration-700 group-hover:grayscale-0 group-hover:sepia-0 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0d0608]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <span className="font-script absolute bottom-3 left-4 text-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ color: "var(--rose)" }}>
              {img.caption}
            </span>
          </motion.button>
        ))}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 md:p-10"
            style={{ background: "rgba(13,6,8,0.9)" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            data-testid="lightbox"
          >
            <button
              className="absolute top-6 right-6 p-2 rounded-full glass"
              onClick={() => setActive(null)}
              data-testid="lightbox-close"
              aria-label="Close"
            >
              <X size={22} style={{ color: "var(--alabaster)" }} />
            </button>
            <motion.figure
              className="max-w-5xl w-full"
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={active.url}
                alt={active.caption}
                className="w-full max-h-[80vh] object-contain rounded-2xl glow-rose"
              />
              <figcaption className="font-script text-2xl text-center mt-5" style={{ color: "var(--rose)" }}>
                {active.caption}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
